/**
 * TRIAL UTILITIES
 * 
 * Helper functions for determining trial listing status and restrictions.
 * Trial users sit on the `free` tier (the default for new accounts). The legacy
 * `one_off_kit`-without-purchase state is still treated as a trial for older accounts.
 */

import { UserBillingState, SubscriptionTier, TIER_CONFIGS, TRIAL_LIMITS } from '@/types/billing';

/**
 * Lifetime listing count for the account. Trial eligibility must be based on
 * "has this account ever created a listing", not on the rolling 30-day usage
 * window (`activeListingsCount`), which resets and would hand out a new free
 * trial listing every month.
 */
function totalListings(billing: UserBillingState): number {
  return Math.max(billing.lifetimeListingsCount ?? 0, billing.activeListingsCount ?? 0);
}

/**
 * Tiers that represent an account that has never paid.
 */
function isUnpaidTier(tier: SubscriptionTier): boolean {
  return tier === 'free' || tier === 'one_off_kit';
}

/**
 * Check if user is on a trial (free tier without active purchases)
 * Trial users get 1 free property with watermarked photos and non-copyable descriptions
 */
export function isTrialUser(billing: UserBillingState | null): boolean {
  if (!billing) return true; // No billing data = trial
  
  // One-off kit without purchase is a trial user
  if (isUnpaidTier(billing.subscriptionTier)) {
    // Check if they've made any purchases
    if (billing.oneOffKitPurchased) return false;
    // Must have 0 or 1 listings (the trial listing)
    return totalListings(billing) <= 1;
  }
  
  // Active subscription = not trial
  if (billing.subscriptionStatus === 'active') return false;
  
  // Inactive/cancelled subscriptions = trial
  return true;
}

/**
 * Check if user can still create a trial listing
 * (has not used their one free trial yet)
 */
export function canCreateTrialListing(billing: UserBillingState | null): boolean {
  if (!billing) return true; // No billing = can create trial
  
  // Only one_off_kit users without purchases can create trial
  if (!isUnpaidTier(billing.subscriptionTier)) return false;
  if (billing.oneOffKitPurchased) return false;
  
  // Can create trial only if they have no listings yet
  return totalListings(billing) === 0;
}

/**
 * Check if user has already used their trial
 */
export function hasUsedTrial(billing: UserBillingState | null): boolean {
  if (!billing) return false;
  
  // One-off kit users who have created at least 1 listing have used trial
  if (isUnpaidTier(billing.subscriptionTier) && !billing.oneOffKitPurchased) {
    return totalListings(billing) >= 1;
  }
  
  return false;
}

/**
 * Get trial limits for display
 */
export function getTrialLimits() {
  return TRIAL_LIMITS;
}

/**
 * Check if a specific listing is a trial listing
 * This is determined by the is_trial flag on the listing
 */
export function isListingTrial(listing: { is_trial?: boolean } | null): boolean {
  if (!listing) return false;
  return listing.is_trial === true;
}

/**
 * Determine if a new listing should be marked as trial
 * Based on user's subscription status and listing count
 */
export function shouldMarkListingAsTrial(billing: UserBillingState | null): boolean {
  if (!billing) return true;
  
  // Paid Single Property Kit listings are never trials
  if (billing.oneOffKitPurchased) return false;

  // Free / unpaid accounts get 1 trial listing
  if (isUnpaidTier(billing.subscriptionTier)) {
    // First listing is trial, subsequent need payment
    return totalListings(billing) === 0;
  }

  
  // Active subscribers don't get trial listings
  if (billing.subscriptionStatus === 'active') return false;
  
  // Users with inactive subscriptions get trial on first listing
  return totalListings(billing) === 0;
}

/**
 * Get the trial restrictions text for display
 */
export function getTrialRestrictionsText(): string {
  return 'Trial listings include watermarked photos and view-only descriptions. Subscribe to unlock full features.';
}

/**
 * Check if user can upgrade from trial
 */
export function canUpgradeFromTrial(billing: UserBillingState | null): boolean {
  return isTrialUser(billing);
}
